// src/pages/usa/colorado/Steamboat.js
import "../../../App.css";
import Header from "../../../components/Header";
import Footer from "../../../components/Footer";

import BackOnly from "../../../components/BackOnly";
import ContactPopup from "../../../components/ContactPopup";
import React, { useState, useEffect } from "react";

import { useNavigate } from "react-router-dom";   // ⭐ REQUIRED

import AffiliateBooking from "../../../components/affiliate/AffiliateBooking";
import AffiliateExpedia from "../../../components/affiliate/AffiliateExpedia";
import AffiliateTrip from "../../../components/affiliate/AffiliateTrip";

// ⭐ STRICT COMMAND: Steamboat ALWAYS goes back to Colorado map
export const backRoute = "/map/colorado";

export default function Steamboat() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // ⭐ Send strict back command to BackOnly (NO LOOPS)
  useEffect(() => {
    if (!window.__steamboatBackRouteSet) {
      window.__steamboatBackRouteSet = true;
      navigate(".", { replace: true, state: { backRoute } });
    }
  }, [navigate]);

  return (

    <>
      <Header />

      <div className="visited-back-wrapper">
        <BackOnly />
      </div>


      <div className="page-container" style={{ padding: "12px" }}>
        <h1>Steamboat, Colorado</h1>

        <section className="travel-review">
          {/* LEFT COLUMN — TEXT */}
          <div className="travel-text">
            <h2>🏞️ Discover Steamboat, Colorado</h2>

            <p>
              Steamboat Springs sits at 6,900 feet above sea level, nestled in Colorado’s Yampa Valley.
              The summit of Mount Werner reaches 10,568 feet, offering expansive views and legendary
              Champagne Powder® snow. The town blends ski culture with ranching roots, creating a
              laid‑back atmosphere that’s both welcoming and rugged.
            </p>

            <p>
              Surrounding Steamboat are several character‑rich towns. Hayden hosts the regional airport.
              Oak Creek leads to Stagecoach Reservoir and the Flat Tops Wilderness. Craig is known for
              wildlife viewing, especially wild mustangs in Sandwash Basin. Clark and Yampa offer quiet
              escapes with alpine lakes and fall colors.
            </p>

            <p>
              Further out, Walden earns its title as the “Moose Capital of Colorado,” while Kremmling and
              Phippsburg offer river recreation and railroad heritage. Each town adds depth to the region’s
              story — perfect for travelers seeking more than just ski slopes.
            </p>

            {/* GETTING THERE */}
            <h3>🚗 Getting There</h3>

            <p>
              Most travelers begin their journey by landing at Denver International Airport (DEN). From
              there, you can drive through scenic mountain passes, take a shuttle, or connect via a short
              flight to Yampa Valley Regional Airport (HDN), just 22 miles from Steamboat. The drive from
              Denver offers breathtaking views and a smooth route into the Yampa Valley.
            </p>

            {/* PERSONAL OBSERVATION */}
            <h3>🌟 My Personal Observation</h3>

            <ul>
              <li>
                <div className="place-block">
                  <strong>Fish Creek Falls:</strong> I’ve visited in winter, fall, and spring — and each season
                  transforms the view completely. The lower falls trail is short but can be slippery. The
                  upper falls trail is about 2.5 miles one way through shaded forest.
                  <br />
                  <a
                    href="https://www.google.com/maps?q=40.48199443266981,-106.77272659394404"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Lower Fish Creek Falls
                  </a>
                  <br />
                  <a
                    href="https://www.google.com/maps?q=40.475384105146716,-106.75083766067644"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Upper Fish Creek Falls
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Strawberry Hot Springs:</strong> A rustic, forested hot spring far from city lights.
                  The road is steep and unpaved, especially in winter. After dark, the springs become clothing
                  optional and adults‑only.
                  <br />
                  <a
                    href="https://www.google.com/maps?q=40.559606734269664,-106.84963374478455"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Strawberry Hot Springs
                  </a>
                </div>
              </li>
            </ul>

            {/* HIDDEN GEMS */}
            <h3>⛰️ Hidden Gems in Steamboat</h3>

            <ul>
              <li>
                <div className="place-block">
                  <strong>Steamboat Lake State Park:</strong> A scenic escape perfect for kayaking,
                  paddleboarding, and wildflower hikes with sunrise views of Hahn’s Peak.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Spring Creek Trail:</strong> A peaceful, shaded trail ideal for morning runs and
                  quiet reflection.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Yampa River Botanic Park:</strong> A free garden loved by locals for picnics,
                  birdwatching, and summer concerts.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Howelsen Hill:</strong> The oldest operating ski area in North America and a summer
                  hub for mountain biking and hiking.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>F.M. Light &amp; Sons:</strong> A historic Western wear shop operating since 1905.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Routt National Forest Backroads:</strong> Scenic drives through aspen groves and
                  meadows, especially magical in fall.
                </div>
              </li>
            </ul>

            {/* PLACES TO STAY */}
            <h3>🛏️ Places to Stay in Steamboat</h3>

            <p>
              Steamboat Springs offers lodging ranging from slope‑side luxury to cozy mountain retreats.
              These options consistently earn high marks on Booking, Expedia, and Google Reviews:
            </p>

            <ul>
              <li>
                <div className="place-block">
                  <strong>Highmark Steamboat Springs:</strong> Upscale suites with full kitchens and mountain views.
                </div>
              </li>
              <li>
                <div className="place-block">
                  <strong>Holiday Inn Steamboat Springs:</strong> Indoor/outdoor pools and a free ski shuttle.
                </div>
              </li>
              <li>
                <div className="place-block">
                  <strong>Sheraton Steamboat Resort Villas:</strong> Ski‑in/ski‑out luxury with spa services.
                </div>
              </li>
              <li>
                <div className="place-block">
                  <strong>Steamboat Grand:</strong> Spacious rooms, outdoor pool, and full‑service spa.
                </div>
              </li>
              <li>
                <div className="place-block">
                  <strong>Chateau Chamonix:</strong> Direct ski access and cozy alpine charm.
                </div>
              </li>
              <li>
                <div className="place-block">
                  <strong>Legacy Vacation Resorts Hilltop:</strong> Relaxed suites with mountain views.
                </div>
              </li>
              <li>
                <div className="place-block">
                  <strong>The Ranch at Steamboat:</strong> Condo‑style lodging with hot tubs and tennis courts.
                </div>
              </li>
              <li>
                <div className="place-block">
                  <strong>Village at Steamboat Springs:</strong> Modern units with full kitchens and pools.
                </div>
              </li>
            </ul>

            {/* AFFILIATE BLOCK */}
           <h3>If you are looking for a place to stay, you can check prices, date and time here.</h3>
                       <div className="affiliate-block">
                         <AffiliateExpedia />
                         <AffiliateBooking />
                         <AffiliateTrip />
                       </div>

            {/* VIDEOS */}
            <h3>🎥 My Travel Videos</h3>

            <p>Here is a video I took from Fish Creek Falls.</p>

            <p>
              <a
                href="https://youtu.be/XPcS9E7gH6Q?si=xBLlIqoKaY6qXnjj"
                target="_blank"
                rel="noopener noreferrer"
              >
                Fish Creek Falls
              </a>
            </p>
          </div>

          {/* RIGHT COLUMN — IMAGES */}
          <div className="travel-image-column">
            <a
              href="https://youtu.be/XPcS9E7gH6Q?si=xBLlIqoKaY6qXnjj"
              target="_blank"
              rel="noopener noreferrer"
              className="youtube-image-card"
            >
              <img
                src="/images/usa/colorado/steamboat/bridgeinwinter.jpg"
                alt="Fish Creek Falls Video"
              />
            </a>

            <img src="/images/usa/colorado/steamboat/bridge.jpg" alt="Bridge" />
            <img src="/images/usa/colorado/steamboat/bridgefromdistance.jpg" alt="Bridge from distance" />
            <img src="/images/usa/colorado/steamboat/eee.jpg" alt="Fish Creek Falls" />
            <img src="/images/usa/colorado/steamboat/thewaterfall2.jpg" alt="The Waterfall" />
            <img src="/images/usa/colorado/steamboat/upperfishcreekfalls.jpg" alt="Upper Fish Creek Falls" />
            <img src="/images/usa/colorado/steamboat/thewaterfall.jpg" alt="Waterfall" />
            <img src="/images/usa/colorado/steamboat/viewofthemountain.jpg" alt="Mountain View" />
            <img src="/images/usa/colorado/steamboat/treesinfall.jpg" alt="Trees in Fall" />
            <img src="/images/usa/colorado/steamboat/thunderheadbalcony.jpg" alt="Thunderhead Balcony" />
            <img src="/images/usa/colorado/steamboat/thunderhead.jpg" alt="Thunderhead" />
            <img src="/images/usa/colorado/steamboat/thunderheadbackview.jpg" alt="Thunderhead Back View" />
            <img src="/images/usa/colorado/steamboat/strawberryhotspringssummer.jpg" alt="Strawberry Hot Springs Summer" />
            <img src="/images/usa/colorado/steamboat/strawberryhotspringswinter.jpg" alt="Strawberry Hot Springs Winter" />
            <img src="/images/usa/colorado/steamboat/strawberryhotspringswinter2.jpg" alt="Strawberry Hot Springs Winter 2" />
          </div>
        </section>
      </div>

      {/* EMAIL BUTTON */}
      <div className="email-wrapper">
        <button className="email-link" onClick={() => setOpen(true)}>
          contact@thefindingspot.com
        </button>
      </div>

      {/* POPUP */}
      <ContactPopup isOpen={open} onClose={() => setOpen(false)} />

      <Footer />
    </>
  );
}
